/**
 * Quality Manager
 * Manages adaptive quality control for multi-device WebRTC streaming
 */

import type {
  StreamQuality,
  MultiDeviceStats,
  DeviceStats,

} from './types';
import { DEFAULT_QUALITY_ADAPTATION, type QualityAdaptationConfig } from './types';
interface SystemResources {
  availableMemory: number; // bytes
  totalBandwidth: number;  // bytes/sec
  deviceCount: number;
  connectionCount: number;
}

/**
 * Quality adaptation decision
 */
export interface QualityDecision {
  psn: string;
  currentQuality: StreamQuality;
  recommendedQuality: StreamQuality;
  reason: string;
  confidence: number; // 0-1
}

/**
 * Quality Manager Class
 * Provides intelligent quality adaptation based on system resources and network conditions
 */
export class QualityManager {
  private config: QualityAdaptationConfig;
  private adaptationHistory: Map<string, StreamQuality[]> = new Map();
  private lastAdaptationTime: Map<string, number> = new Map();
  private readonly hysteresisBuffer = 0.2; // 20% buffer to prevent oscillation
  
  constructor(config: QualityAdaptationConfig = DEFAULT_QUALITY_ADAPTATION) {
    this.config = config;
  }
  
  /**
   * Analyze current system state and recommend quality adjustments
   * @param globalStats - Current global statistics
   * @param deviceStats - Individual device statistics
   * @returns Array of quality decisions for each device
   */
  analyzeAndRecommend(
    globalStats: MultiDeviceStats, 
    deviceStats: DeviceStats[]
  ): QualityDecision[] {
    if (!this.config.enableAdaptation) {
      return [];
    }
    
    // Extract system resources
    const systemResources: SystemResources = {
      availableMemory: this.estimateAvailableMemory(globalStats.memoryUsage),
      totalBandwidth: globalStats.totalBandwidth,
      deviceCount: globalStats.totalDevices,
      connectionCount: globalStats.totalConnections
    };
    
    // Generate recommendations for each device
    const decisions: QualityDecision[] = [];
    
    deviceStats.forEach(device => {
      const decision = this.evaluateDeviceQuality(device, systemResources);
      if (decision) {
        decisions.push(decision);
      }
    });
    
    // Apply global constraints
    const constrainedDecisions = this.applyGlobalConstraints(decisions, systemResources);
    
    return constrainedDecisions;
  }
  
  /**
   * Evaluate quality for a specific device
   * @param device - Device statistics
   * @param systemResources - Current system resources
   * @returns Quality decision or null if no change needed
   */
  private evaluateDeviceQuality(
    device: DeviceStats, 
    systemResources: SystemResources
  ): QualityDecision | null {
    
    // Get current quality (assume medium if not available)
    const currentQuality: StreamQuality = this.getCurrentQuality(device.psn);
    
    // Check if we should skip this device due to recent changes
    if (this.shouldSkipAdaptation(device.psn)) {
      return null;
    }
    
    // Evaluate based on different criteria
    const deviceCountRecommendation = this.evaluateByDeviceCount(systemResources.deviceCount);
    const bandwidthRecommendation = this.evaluateByBandwidth(systemResources.totalBandwidth);
    const memoryRecommendation = this.evaluateByMemory(systemResources.availableMemory);
    
    // Determine the most restrictive quality (lowest quality wins)
    const recommendations = [
      deviceCountRecommendation,
      bandwidthRecommendation, 
      memoryRecommendation
    ].filter(Boolean);
    
    const recommendedQuality = this.selectMostRestrictiveQuality(recommendations);
    
    // Check if change is significant enough
    if (currentQuality === recommendedQuality) {
      return null;
    }
    
    // Apply hysteresis to prevent rapid changes
    if (!this.passesHysteresisCheck(device.psn, currentQuality, recommendedQuality)) {
      return null;
    }
    
    // Generate decision
    const decision: QualityDecision = {
      psn: device.psn,
      currentQuality,
      recommendedQuality,
      reason: this.generateReason(recommendations),
      confidence: this.calculateConfidence(recommendations)
    };
    
    // Update adaptation history
    this.updateAdaptationHistory(device.psn, recommendedQuality);
    
    return decision;
  }
  
  /**
   * Evaluate quality based on device count
   */
  private evaluateByDeviceCount(deviceCount: number): StreamQuality | null {
    const rules = this.config.adaptationRules.deviceCount;
    
    for (const rule of rules.sort((a, b) => a.threshold - b.threshold)) {
      if (deviceCount <= rule.threshold) {
        return rule.quality;
      }
    }
    
    return 'low'; // Default to low if exceeds all thresholds
  }
  
  /**
   * Evaluate quality based on bandwidth
   */
  private evaluateByBandwidth(totalBandwidthBytesPerSec: number): StreamQuality | null {
    const bandwidthMbps = (totalBandwidthBytesPerSec * 8) / (1024 * 1024);
    const rules = this.config.adaptationRules.bandwidth;
    
    for (const rule of rules.sort((a, b) => b.threshold - a.threshold)) {
      if (bandwidthMbps >= rule.threshold) {
        return rule.quality;
      }
    }
    
    return 'low'; // Default to low if below all thresholds
  }
  
  /**
   * Evaluate quality based on memory usage
   */
  private evaluateByMemory(availableMemoryBytes: number): StreamQuality | null {
    const availableMemoryMB = availableMemoryBytes / (1024 * 1024);
    const rules = this.config.adaptationRules.memory;
    
    for (const rule of rules.sort((a, b) => b.threshold - a.threshold)) {
      if (availableMemoryMB >= rule.threshold) {
        return rule.quality;
      }
    }
    
    return 'low'; // Default to low if memory is constrained
  }
  
  /**
   * Select the most restrictive (lowest) quality from recommendations
   */
  private selectMostRestrictiveQuality(qualities: (StreamQuality | null)[]): StreamQuality {
    const validQualities = qualities.filter(q => q !== null) as StreamQuality[];
    
    if (validQualities.length === 0) {
      return 'medium'; // Default fallback
    }
    
    // Quality priority (most restrictive first)
    const qualityPriority: Record<StreamQuality, number> = {
      'low': 1,
      'medium': 2,
      'high': 3
    };
    
    return validQualities.reduce((lowest, current) => 
      qualityPriority[current] < qualityPriority[lowest] ? current : lowest
    );
  }
  
  /**
   * Apply global constraints to quality decisions
   */
  private applyGlobalConstraints(
    decisions: QualityDecision[], 
    systemResources: SystemResources
  ): QualityDecision[] {
    
    // Ensure at least one high-quality stream if resources allow
    const highQualityDecisions = decisions.filter(d => d.recommendedQuality === 'high');
    
    if (highQualityDecisions.length === 0 && systemResources.deviceCount <= 2) {
      // Promote one device to high quality
      const candidateDecision = decisions.find(d => d.recommendedQuality === 'medium');
      if (candidateDecision) {
        candidateDecision.recommendedQuality = 'high';
        candidateDecision.reason += ' (promoted for better viewing experience)';
      }
    }
    
    // Limit high-quality streams based on resources
    const maxHighQualityStreams = this.calculateMaxHighQualityStreams(systemResources);
    const finalHighQualityDecisions = decisions
      .filter(d => d.recommendedQuality === 'high')
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, maxHighQualityStreams);
    
    // Downgrade excess high-quality streams
    const excessHighQuality = decisions
      .filter(d => d.recommendedQuality === 'high')
      .filter(d => !finalHighQualityDecisions.includes(d));
    
    excessHighQuality.forEach(decision => {
      decision.recommendedQuality = 'medium';
      decision.reason += ' (downgraded due to resource constraints)';
    });
    
    return decisions;
  }
  
  /**
   * Calculate maximum number of high-quality streams system can handle
   */
  private calculateMaxHighQualityStreams(resources: SystemResources): number {
    const memoryConstraint = Math.floor(resources.availableMemory / (100 * 1024 * 1024)); // 100MB per high-quality
    const bandwidthConstraint = Math.floor(resources.totalBandwidth / (5 * 1024 * 1024)); // 5MB/s per high-quality
    
    return Math.min(memoryConstraint, bandwidthConstraint, 2); // Max 2 high-quality streams
  }
  
  /**
   * Check if adaptation should be skipped for this device
   */
  private shouldSkipAdaptation(psn: string): boolean {
    const lastAdaptation = this.lastAdaptationTime.get(psn);
    if (!lastAdaptation) return false;
    
    const timeSinceLastAdaptation = Date.now() - lastAdaptation;
    const minAdaptationInterval = 10000; // 10 seconds minimum between adaptations
    
    return timeSinceLastAdaptation < minAdaptationInterval;
  }
  
  /**
   * Check if quality change passes hysteresis check
   */
  private passesHysteresisCheck(
    psn: string, 
    currentQuality: StreamQuality, 
    newQuality: StreamQuality
  ): boolean {
    const history = this.adaptationHistory.get(psn) || [];
    
    // If we've recently oscillated between these qualities, apply hysteresis
    const recentHistory = history.slice(-3);
    const hasOscillation = recentHistory.includes(currentQuality) && 
                          recentHistory.includes(newQuality);
    
    if (hasOscillation) {
      // Require stronger signal to overcome hysteresis
      const qualityDiff = this.getQualityDifference(currentQuality, newQuality);
      return Math.abs(qualityDiff) > (1 + this.hysteresisBuffer);
    }
    
    return true;
  }
  
  /**
   * Get numeric difference between qualities
   */
  private getQualityDifference(quality1: StreamQuality, quality2: StreamQuality): number {
    const qualityValues: Record<StreamQuality, number> = {
      'low': 1,
      'medium': 2,
      'high': 3
    };
    
    return qualityValues[quality2] - qualityValues[quality1];
  }
  
  /**
   * Update adaptation history for a device
   */
  private updateAdaptationHistory(psn: string, quality: StreamQuality): void {
    const history = this.adaptationHistory.get(psn) || [];
    history.push(quality);
    
    // Keep only last 10 adaptations
    if (history.length > 10) {
      history.shift();
    }
    
    this.adaptationHistory.set(psn, history);
    this.lastAdaptationTime.set(psn, Date.now());
  }
  
  /**
   * Generate human-readable reason for quality change
   */
  private generateReason(recommendations: (StreamQuality | null)[]): string {
    const reasons: string[] = [];
    
    if (recommendations.includes('low')) {
      reasons.push('High resource usage detected');
    }
    if (recommendations.includes('medium')) {
      reasons.push('Moderate resource constraints');
    }
    if (recommendations.includes('high')) {
      reasons.push('Sufficient resources available');
    }
    
    return reasons.join(', ') || 'System optimization';
  }
  
  /**
   * Calculate confidence score for quality decision
   */
  private calculateConfidence(recommendations: (StreamQuality | null)[]): number {
    const validRecommendations = recommendations.filter(r => r !== null);
    const agreement = validRecommendations.length > 0 ? 
      validRecommendations.filter(r => r === validRecommendations[0]).length / validRecommendations.length : 0;
    
    return agreement;
  }
  
  /**
   * Get current quality for a device (placeholder implementation)
   */
  private getCurrentQuality(psn: string): StreamQuality {
    // In a real implementation, this would be retrieved from the device manager
    return 'medium'; // Default assumption
  }
  
  /**
   * Estimate available memory based on current usage
   */
  private estimateAvailableMemory(currentUsage: number): number {
    // Estimate total system memory and subtract current usage
    const estimatedTotalMemory = 4 * 1024 * 1024 * 1024; // 4GB assumption
    return Math.max(estimatedTotalMemory - currentUsage, 512 * 1024 * 1024); // Min 512MB
  }
  
  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<QualityAdaptationConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
  
  /**
   * Get adaptation history for a device
   */
  getAdaptationHistory(psn: string): StreamQuality[] {
    return this.adaptationHistory.get(psn) || [];
  }
  
  /**
   * Clear adaptation history
   */
  clearHistory(): void {
    this.adaptationHistory.clear();
    this.lastAdaptationTime.clear();
  }
}

/**
 * Create Quality Manager with default configuration
 */
export function createQualityManager(config?: Partial<QualityAdaptationConfig>): QualityManager {
  return new QualityManager(config ? { ...DEFAULT_QUALITY_ADAPTATION, ...config } : DEFAULT_QUALITY_ADAPTATION);
}